"use client";

import { useState } from "react";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from "@/ui";
import RichTextAIField, { isHtmlEmpty } from "@components/shared/form/ai/RichTextAIField/RichTextAIField";
import { PROFILE_CULTURE_MAX_LENGTH } from "@lib/candidates/saveItemToProfile";
import FormSection from "./parts/FormSection";

type CultureKey = "personality_summary" | "work_style" | "personal_story";

// The same rich-text editor the submission builder uses, so text moves
// between the profile and submissions with its formatting.
const FIELDS: { key: CultureKey; label: string; placeholder: string }[] = [
  {
    key: "personality_summary",
    label: "Personality summary",
    placeholder: "Overall personality summary based on CV analysis...",
  },
  {
    key: "work_style",
    label: "Work style",
    placeholder:
      "How they approach work - pace, methodology, leadership style, and collaboration preferences...",
  },
  {
    key: "personal_story",
    label: "Personal story",
    placeholder:
      "How they have evolved holistically throughout their career, key growth moments and professional development journey...",
  },
];

interface EditCultureProps {
  open: boolean;
  onClose: () => void;
  candidate: Partial<Record<CultureKey, string | null>> & Record<string, unknown>;
  onSave: (candidate: Record<string, unknown>) => void;
}

export default function EditCulture({ onClose, open, candidate, onSave }: EditCultureProps) {
  const [culture, setCulture] = useState<Record<CultureKey, string>>(() => ({
    personality_summary: candidate?.personality_summary || "",
    work_style: candidate?.work_style || "",
    personal_story: candidate?.personal_story || "",
  }));

  // The API caps each field, markup included; checked here so the text isn't
  // lost to a failed save after the dialog closes.
  const tooLong = (key: CultureKey) => culture[key].length > PROFILE_CULTURE_MAX_LENGTH;

  const handleSubmit = () => {
    if (FIELDS.some(({ key }) => tooLong(key))) return;
    // A cleared editor leaves "<p></p>" behind; save that as empty.
    const cleaned = Object.fromEntries(
      FIELDS.map(({ key }) => [key, isHtmlEmpty(culture[key]) ? "" : culture[key]]),
    );
    onSave({ ...candidate, ...cleaned });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit culture & personality</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            {FIELDS.map(({ key, label, placeholder }) => (
              <FormSection key={key}>
                <RichTextAIField
                  id={`culture-${key}`}
                  label={label}
                  value={culture[key]}
                  onChange={(value) => setCulture((current) => ({ ...current, [key]: value }))}
                  placeholder={placeholder}
                  alwaysShowToolbar
                  minHeight={96}
                  prompt={`Improve this candidate ${label.toLowerCase()}. Make it clear, concise, professional, and useful to a hiring manager while keeping all facts accurate.`}
                  error={tooLong(key) ? "Too long to save. Shorten this text." : undefined}
                />
              </FormSection>
            ))}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={FIELDS.some(({ key }) => tooLong(key))}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
